/**
 * Frontend Error Capture — sends JS errors to backend for logging
 */
const API = process.env.REACT_APP_BACKEND_URL;
const ERROR_BUFFER = [];
let flushTimer = null;

function flushErrors() {
  if (ERROR_BUFFER.length === 0) return;
  const errors = ERROR_BUFFER.splice(0, 20);
  const token = sessionStorage.getItem('token');
  fetch(`${API}/api/system/frontend-errors`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ errors }),
  }).catch(() => {});
}

function queueError(error) {
  ERROR_BUFFER.push(error);
  if (flushTimer) clearTimeout(flushTimer);
  // Flush after 2 seconds of inactivity, or immediately if buffer is large
  if (ERROR_BUFFER.length >= 5) {
    flushErrors();
  } else {
    flushTimer = setTimeout(flushErrors, 2000);
  }
}

function getUserInfo() {
  try {
    const token = sessionStorage.getItem('token');
    if (!token) return {};
    const payload = JSON.parse(atob(token.split('.')[1]));
    return { user_id: payload.sub, user_email: payload.email };
  } catch (e) {
    return {};
  }
}

export function initErrorCapture() {
  // Capture unhandled JS errors
  window.addEventListener('error', (event) => {
    queueError({
      type: 'runtime_error',
      message: event.message,
      stack: event.error?.stack || `${event.filename}:${event.lineno}:${event.colno}`,
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      ...getUserInfo(),
    });
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    queueError({
      type: 'unhandled_rejection',
      message: reason?.message || String(reason),
      stack: reason?.stack || '',
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      ...getUserInfo(),
    });
  });

  // Capture console.error calls
  const originalConsoleError = console.error;
  console.error = (...args) => {
    originalConsoleError.apply(console, args);
    const message = args.map(a => {
      if (a instanceof Error) return a.message;
      if (typeof a === 'object') try { return JSON.stringify(a); } catch (e) { return String(a); }
      return String(a);
    }).join(' ');

    // Skip React dev warnings, noise, and third-party API errors
    if (message.includes('Warning:') || message.includes('DevTools') || message.includes('Download the React DevTools')
      || message.includes('Google Maps') || message.includes('InvalidKey') || message.includes('InvalidKeyMapError')) return;

    queueError({
      type: 'console_error',
      message: message.substring(0, 2000),
      stack: new Error().stack || '',
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      ...getUserInfo(),
    });
  };

  // Flush on page unload
  window.addEventListener('beforeunload', flushErrors);
}
